import '@beyondtrust/btux-iconography-web';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import styles from './nav-section.styles.scss';

export const NAV_SECTION_TAG = 'platform-nav-section' as const;

/**
 * Accordion section for navigation menus. Consumers building slotted menu
 * replacements use it for the platform look and feel, and the sidebar panel
 * renders API-driven feature sets through it — one implementation, so the
 * underlying machinery (currently Shoelace's sl-details) can change without
 * breaking consumers.
 *
 * The API is deliberately small: `heading` (or the `heading` slot for rich
 * content), an optional `icon`, and `open` (reflected; stays in sync when the
 * user toggles the section). Items go in the default slot.
 *
 * @public
 */
@customElement(NAV_SECTION_TAG)
export class PlatformNavSection extends LitElement {
  static override styles = [styles];

  /** Section title. Use the `heading` slot instead for rich content. @public */
  @property()
  heading = '';

  /** Optional btux-svg icon name shown before the heading. @public */
  @property()
  icon = '';

  /** Expanded state; reflects user toggling. @public */
  @property({ type: Boolean, reflect: true })
  open = false;

  // Only track the wrapped details' own events — a nested section's composed
  // sl-show/sl-hide must not flip this one's state.
  private onShow = (event: Event) => {
    if (event.target === event.currentTarget) {
      this.open = true;
    }
  };

  private onHide = (event: Event) => {
    if (event.target === event.currentTarget) {
      this.open = false;
    }
  };

  override render() {
    return html`
      <sl-details ?open=${this.open} @sl-show=${this.onShow} @sl-hide=${this.onHide}>
        <span slot="summary" class="heading">
          ${this.icon
            ? keyed(
                // btux-svg only reads its name at first render (a class field shadows
                // the reactive property), so re-create the element instead of mutating it.
                this.icon,
                html`<btux-svg name=${this.icon}></btux-svg>`,
              )
            : // Reserve the icon's footprint so heading text aligns with the
              // section's items (and with iconed siblings) either way.
              html`<span class="icon-spacer"></span>`}
          <slot name="heading">${this.heading}</slot>
        </span>
        <btux-svg slot="expand-icon" name="chevron-right"></btux-svg>
        <btux-svg slot="collapse-icon" name="chevron-right"></btux-svg>
        <slot></slot>
      </sl-details>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [NAV_SECTION_TAG]: PlatformNavSection;
  }
}
